import React, { useEffect, useRef, useState } from 'react';
import { Delete } from '@mui/icons-material';
import type { Delegation } from './type';
import { AddDelegatorDialog } from './AddDelegatorDialog';
import { DELEGATE_TEXT } from '../../lang';
import { useDeleteDelegator, useGetDelegator } from '../../../graphql';
import { useAuth } from '../../../../../contexts';
import {
  Alert,
  type AlertDetails,
  Button,
  type HeadCell,
  IconButton,
  Loading,
  Table,
} from '../../../../../components';

export type DelegateListProps = {
  filter?: any;
};

export const DelegateList: React.FunctionComponent<DelegateListProps> = (
  props: DelegateListProps,
) => {
  const [openDialog, setOpenDialog] = useState(false);
  const previousDelegatorRef = useRef<Delegation[]>([]);
  const { user } = useAuth();
  const { userInfo } = user ?? {};
  const { netId = '' } = userInfo ?? {};
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const [visibleData, setVisibleData] = useState<Delegation[]>([]);

  const {
    delegator,
    error,
    loading,
    refetch: refetchDelegatorList,
  } = useGetDelegator(netId);
  refetchDelegatorList();

  const dataCells: Array<HeadCell<Delegation>> = [
    {
      id: 'name',
      label: DELEGATE_TEXT.NAME,
      isSortable: false,
      render: (row: Delegation) => row.name,
    },
    {
      id: 'department',
      label: DELEGATE_TEXT.NET_ID,
      isSortable: false,
      render: (row: Delegation) => row.netId,
    },
    {
      id: 'email',
      label: DELEGATE_TEXT.EMAIL,
      isSortable: false,
      render: (row: Delegation) => row.email,
    },
    {
      id: 'department',
      label: DELEGATE_TEXT.DEPARTMENT,
      isSortable: false,
      render: (row: Delegation) => row.department,
    },
    {
      id: 'startDate',
      label: DELEGATE_TEXT.START_DATE,
      isSortable: false,
      render: (row: Delegation) => row.startDate,
    },
    {
      id: 'action',
      label: DELEGATE_TEXT.ACTION,
      isSortable: false,
      minWidth: 10,
      render: (row: Delegation) => action(row),
    },
  ];

  const action = (row: Delegation): React.ReactNode => {
    const { netId } = row;
    return (
      <IconButton
        onClick={handleDeleteDelegator(netId)}
        color="primary"
        icon={<Delete />}
      />
    );
  };

  useEffect(() => {
    if (!loading) {
      setVisibleData(delegator);
    }
  }, [loading]);

  useEffect(() => {
    if (previousDelegatorRef.current.length !== delegator.length) {
      setVisibleData(delegator);
      previousDelegatorRef.current = delegator;
    }
  }, [delegator]);

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const handleOpenDialog = (): void => {
    setOpenDialog(true);
  };
  const handleAddDelegatorSuccess = (): void => {
    setAlertDetails({
      alertContent: `Added the Delegator Successfully.`,
      alertType: 'success',
    });
    setOpenAlert(true);
  };
  const handleCloseDialog = (): void => {
    setOpenDialog(false);
  };

  const deleteDelegator = useDeleteDelegator(refetchDelegatorList);
  const handleDeleteDelegator =
    (deleteNetId: string) => async (): Promise<void> => {
      const variables = {
        input: {
          from: netId,
          to: deleteNetId,
          role: [DELEGATE_TEXT.DIALOG_TEXT.SUPERVISOR],
        },
      };

      try {
        await deleteDelegator({
          variables,
        });
        setAlertDetails({
          alertContent: `Deleted the Delegator Successfully.`,
          alertType: 'success',
        });
      } catch (error: any) {
        setAlertDetails({
          alertContent: `Error Occurs.` + (error as Error).message,
          alertType: 'error',
        });
      }
      setOpenAlert(true);
    };
  return (
    <div className="w-full">
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      {openDialog && (
        <AddDelegatorDialog
          refetchDelegatorList={refetchDelegatorList}
          open={openDialog}
          onClose={handleCloseDialog}
          onAddSuccess={handleAddDelegatorSuccess}
          currentNetId={netId}
        />
      )}
      <div className="flex flex-row-reverse">
        <Button
          variant="contained"
          sx={{
            textTransform: 'none',
            fontSize: '1rem',
          }}
          onClick={handleOpenDialog}
        >
          {DELEGATE_TEXT.ADD_DELEGATOR}
        </Button>
      </div>

      {loading ? (
        <Loading />
      ) : (
        <div className="mt-4">
          <Table<Delegation>
            headCells={dataCells}
            data={visibleData}
            handleRequestSort={() => {}}
          />
        </div>
      )}
    </div>
  );
};
